import React from 'react';
import { connect } from 'react-redux';
import DayPicker from 'react-day-picker';
import format from 'date-fns/format';

import { AppSelectors } from 'app/shared/selectors';
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';

import {
    Adornment,
    AdornedInput,
    Flex,
    Label,
    IconCalendarOutline,
    Spacer,
    Input,
    TextButton,
} from '@zillow/constellation';

import { StyledDateCalendarContainer, StyledDateCalendarPicker } from './StyledComponents';

// Stylesheets
import 'react-day-picker/lib/style.css';
import './style.scss';

class DateCalendar extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            openCalendar: false,
            displayValue: '',
            today: '',
            selectedDate: '',
            nextMonth: '',
        };

        this.dayPickerNode = React.createRef();
        this.toggleCalendar = this.toggleCalendar.bind(this);
        this.handleDateSelect = this.handleDateSelect.bind(this);
        this.renderDayPicker = this.renderDayPicker.bind(this);
        this.handleMousedown = this.handleMousedown.bind(this);
    }

    componentDidMount() {
        const { dateAvailable } = this.props;
        const today = new Date();
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1);
        const previouslySelectedDateAvailable = dateAvailable && new Date(`${dateAvailable}T10:00:00`);

        // Some DayPicker props require either Date objects or null
        let selectedDate = null;
        let formattedDate = '';

        if (previouslySelectedDateAvailable >= today) {
            formattedDate = format(this.props.dateAvailable, 'MM/DD/YYYY');
            // Adding "T10:00:00" as a cushion so that when
            //  2017-06-06 is converted to date Object, it isn't
            //  converted to a date object from 2017-06-05
            selectedDate = new Date(`${this.props.dateAvailable}T10:00:00`);
        } else {
            formattedDate = format(today, 'MM/DD/YYYY');
            selectedDate = today;
        }

        window.addEventListener('mousedown', this.handleMousedown);

        this.setState({
            today,
            displayValue: formattedDate,
            selectedDate,
            nextMonth,
        });
    }

    componentWillUnmount() {
        window.removeEventListener('mousedown', this.handleMousedown);
    }

    handleMousedown(e) {
        if (this.node.contains(e.target) || !this.state.openCalendar) {
            return;
        }

        this.toggleCalendar();
    }

    onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER) {
            event.target.click();
        }
    }

    handleDateSelect(day, modifiers) {
        if (modifiers.disabled) {
            return;
        }

        const formattedDate = format(new Date(day), 'MM/DD/YYYY');
        const formattedDateForApi = format(new Date(day), 'YYYY-MM-DD');

        this.setState({
            selectedDate: day,
            displayValue: formattedDate,
            openCalendar: false,
        });

        this.props.saveFloorplanData('dateAvailable', formattedDateForApi, 0);
    }

    toggleCalendar() {
        this.setState((prevState) => {
            if (!prevState.openCalendar && this.props.isScreenMobile) {
                this.dayPickerNode.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
            return ({ openCalendar: !prevState.openCalendar });
        });
    }

    renderDayPicker() {
        const { state } = this;
        const modifiers = {
            disabled: (d1) => {
                const d2 = state.today;
                const diffBetweenYears = d1.getFullYear() - d2.getFullYear();

                if (diffBetweenYears === 1) {
                    if (d1.getMonth() > d2.getMonth()) {
                        return true;
                    }
                    if (d1.getMonth() === d2.getMonth()) {
                        return d1.getDate() > d2.getDate();
                    }
                    return false;
                }
                if (diffBetweenYears === 0) {
                    if (d1.getMonth() < d2.getMonth()) {
                        return true;
                    }
                    if (d1.getMonth() === d2.getMonth()) {
                        return d1.getDate() < d2.getDate();
                    }
                    return false;
                }
                return true;
            },
            selected: (d1) => {
                const d2 = state.today;

                return d1.getDate() === d2.getDate() &&
                    d1.getMonth() === d2.getMonth() &&
                    d1.getFullYear() === d2.getFullYear();
            },
        };

        return (
            <StyledDateCalendarPicker>
                <DayPicker
                    selectedDays={state.selectedDate}
                    enableOutsideDays={true}
                    initialMonth={state.today}
                    month={state.selectedDate}
                    numberOfMonths={1}
                    modifiers={modifiers}
                    onDayClick={this.handleDateSelect}
                />
            </StyledDateCalendarPicker>
        );
    }

    render() {
        const { isDisabled } = this.props;

        return (
            // can only attach ref to HTML element
            <StyledDateCalendarContainer ref={(node) => { this.node = node; }}>
                <div className="DateCalendar">
                    <div className="DateCalendar-input-container">
                        <Label>Date available</Label>
                        <Spacer marginY="xs" />
                        <Flex display="flex" alignItems="center">
                            <AdornedInput
                                inputId="DateAvailable"
                                aria-label="Date available"
                                data-test="Date available"
                                name="Date Available"
                                rightAdornment={(
                                    <Adornment onClick={this.toggleCalendar}>
                                        <IconCalendarOutline size="sm" fontColor="brand" />
                                    </Adornment>
                                )}
                                disabled={isDisabled}
                                onClick={this.toggleCalendar}
                                input={<Input value={this.state.displayValue} />}
                            />
                            <Spacer marginX="xs" />
                            <TextButton
                                fontType="bodySmall"
                                onClick={() => (!isDisabled
                                    ? this.handleDateSelect(this.state.nextMonth, {})
                                    : null)
                                }
                                onKeyUp={this.onKeyUp}
                                aria-label="Set to available next month"
                                tabIndex={0}
                                role="button"
                            >
                                Set to
                                {' '}
                                {format(this.state.nextMonth, 'MM/DD/YYYY')}
                            </TextButton>
                        </Flex>
                        <div ref={this.dayPickerNode}>
                            {this.state.openCalendar ? this.renderDayPicker() : ''}
                        </div>
                    </div>
                    <Spacer marginY="sm" />
                </div>
            </StyledDateCalendarContainer>
        );
    }
}

const mapStateToProps = state => ({
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
});

export { DateCalendar };
export default connect(mapStateToProps)(DateCalendar);
